import { useTranslation } from 'react-i18next';

import { ReactComponent as Alert } from 'assets/icons/Alert.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useReactionsContext from 'contexts/ReactionsContext/useReactionsContext';
import useRoomIdentityContext from 'contexts/RoomIdentityContext/useRoomIdentityContext';
import useRoomStateContext from 'contexts/RoomStateContext/useRoomStateContext';
import useToast from 'contexts/ToastProvider/useToast';
import logger from 'utils/logging/faro';
import { getMessengerRoomId } from 'utils/roomHierarchyUtils';

type KickOptions = { includeChat?: boolean };

export enum MatrixMembershipChangeReasons {
  BAN = 'ban',
  KICK = 'kick',
  ACCEPT = 'accept',
  MOVE = 'move',
  UNASSIGNED = 'unassigned',
  MODERATOR = 'moderator',
}

const useManageMatrixRoomUsers = () => {
  const { t } = useTranslation();
  const { pushToast } = useToast();
  const { matrixClient } = useMatrixContext();
  const { mxRtcSession, chatRoomId } = useRoomStateContext();
  const { raisedHands } = useReactionsContext();
  const {
    state: { roomId },
  } = useRoomIdentityContext();

  const pushInfoToast = (description: string) =>
    pushToast({ title: t('something_wrong'), description, icon: Alert, variant: 'info' });

  const kick = async (matrixUserId: string, options?: KickOptions) => {
    const room = mxRtcSession?.room;
    const raiseHandEventId = raisedHands.get(matrixUserId)?.raiseHandEventId;
    //NOTE: removing raised hand for participant, if he has one, because he might not be able to do so himself (when membership !== join)
    if (room && raiseHandEventId) {
      room.client.redactEvent(room.roomId, raiseHandEventId).catch((error) => {
        logger.error(`Failed to redact reaction event for reactionId ${raiseHandEventId}`, { error });
      });
    }

    try {
      await matrixClient.kick(roomId, matrixUserId, MatrixMembershipChangeReasons.KICK);

      const callRoom = matrixClient.getRoom(roomId);
      const isMessengerLinked = !!getMessengerRoomId(callRoom);
      const shouldKickChat = options?.includeChat ?? !isMessengerLinked;
      if (shouldKickChat && chatRoomId) {
        await matrixClient.kick(chatRoomId, matrixUserId, MatrixMembershipChangeReasons.KICK);
      }
    } catch (error) {
      logger.error(`Error while trying to kick participant(id: ${matrixUserId}): `, error);
      pushInfoToast(t('participant_was_not_kicked'));
    }
  };

  const ban = async (matrixUserId: string) => {
    try {
      await matrixClient.ban(roomId, matrixUserId, MatrixMembershipChangeReasons.BAN);

      if (chatRoomId) await matrixClient.ban(chatRoomId, matrixUserId, MatrixMembershipChangeReasons.BAN);
    } catch (error) {
      logger.error(`Error while trying to ban participant(id: ${matrixUserId}): `, error);
      pushInfoToast(t('participant_was_not_banned'));
    }
  };

  const accept = (matrixUserId: string) =>
    matrixClient.invite(roomId, matrixUserId, MatrixMembershipChangeReasons.ACCEPT).catch((e) => {
      logger.error(`Error while trying to accept participant(id: ${matrixUserId}): `, e);
      pushInfoToast(t('participant_was_not_accepted'));
    });

  return { kick, ban, accept };
};

export default useManageMatrixRoomUsers;
